import {
  ARRAY_OPERATORS,
  isRuleGroup,
  type ListCombinator,
  type ListDefinition,
  type ListFieldType,
  type ListOperator,
  type ListRule,
  type ListRuleGroup,
  operatorsForField,
  PERSON_LIST_FIELDS,
  PERSON_LIST_FIELD_BY_KEY,
  UNARY_OPERATORS,
} from '@fc/core'
import { Button } from '@fc/ui'
import { Plus, Trash2 } from 'lucide-react'

const OPERATOR_LABELS: Record<ListOperator, string> = {
  eq: 'is',
  neq: 'is not',
  contains: 'contains',
  starts_with: 'starts with',
  gt: 'greater than',
  gte: 'at least',
  lt: 'less than',
  lte: 'at most',
  before: 'before',
  after: 'after',
  in: 'is any of',
  not_in: 'is none of',
  is_set: 'is set',
  is_not_set: 'is not set',
  is_true: 'is true',
  is_false: 'is false',
}

function fieldType(fieldKey: string): ListFieldType {
  return PERSON_LIST_FIELD_BY_KEY[fieldKey]?.type ?? 'text'
}

function defaultOperator(fieldKey: string): ListOperator {
  const type = fieldType(fieldKey)
  return operatorsForField(fieldKey, type)[0] ?? 'eq'
}

const FIELD_GROUPS = Array.from(new Set(PERSON_LIST_FIELDS.map((f) => f.group)))

// ---- a single rule row ------------------------------------------------------

function RuleRow({
  rule,
  onChange,
  onRemove,
}: {
  rule: ListRule
  onChange: (next: ListRule) => void
  onRemove: () => void
}) {
  const def = PERSON_LIST_FIELD_BY_KEY[rule.field]
  const type = def?.type ?? 'text'
  const operators = operatorsForField(rule.field, type)
  const isUnary = UNARY_OPERATORS.has(rule.operator)
  const isArray = ARRAY_OPERATORS.has(rule.operator)

  const selectClass =
    'h-9 rounded-lg border border-border bg-card px-2.5 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring'
  const inputClass =
    'h-9 min-w-0 flex-1 rounded-lg border border-border bg-card px-3 text-sm text-fg placeholder:text-dim focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring'

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border-soft bg-bg-alt p-2">
      {/* field */}
      <label className="sr-only" htmlFor={`field-${rule.field}`}>
        Field
      </label>
      <select
        id={`field-${rule.field}`}
        className={selectClass}
        value={rule.field}
        onChange={(e) => {
          const nextField = e.target.value
          onChange({ field: nextField, operator: defaultOperator(nextField), value: '' })
        }}
      >
        {FIELD_GROUPS.map((group) => (
          <optgroup key={group} label={group}>
            {PERSON_LIST_FIELDS.filter((f) => f.group === group).map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      {/* operator */}
      <label className="sr-only">Operator</label>
      <select
        className={selectClass}
        value={rule.operator}
        onChange={(e) => {
          const nextOp = e.target.value as ListOperator
          const next: ListRule = { ...rule, operator: nextOp }
          if (UNARY_OPERATORS.has(nextOp)) next.value = null
          else if (ARRAY_OPERATORS.has(nextOp) && !Array.isArray(next.value)) next.value = []
          else if (!ARRAY_OPERATORS.has(nextOp) && Array.isArray(next.value)) next.value = ''
          onChange(next)
        }}
      >
        {operators.map((op) => (
          <option key={op} value={op}>
            {OPERATOR_LABELS[op]}
          </option>
        ))}
      </select>

      {/* value */}
      {isUnary ? null : isArray ? (
        <input
          className={inputClass}
          placeholder="Comma-separated values"
          aria-label="Values"
          value={Array.isArray(rule.value) ? rule.value.join(', ') : ''}
          onChange={(e) =>
            onChange({
              ...rule,
              value: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
        />
      ) : type === 'enum' && def?.options ? (
        <select
          className={selectClass}
          aria-label="Value"
          value={typeof rule.value === 'string' ? rule.value : ''}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
        >
          <option value="" disabled>
            Select…
          </option>
          {def.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : type === 'date' ? (
        <input
          type="date"
          className={inputClass}
          aria-label="Value"
          value={typeof rule.value === 'string' ? rule.value : ''}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
        />
      ) : type === 'number' ? (
        <input
          type="number"
          className={inputClass}
          aria-label="Value"
          value={
            typeof rule.value === 'number'
              ? rule.value
              : typeof rule.value === 'string'
                ? rule.value
                : ''
          }
          onChange={(e) =>
            onChange({ ...rule, value: e.target.value === '' ? '' : Number(e.target.value) })
          }
        />
      ) : (
        <input
          className={inputClass}
          aria-label="Value"
          placeholder="Value"
          value={typeof rule.value === 'string' ? rule.value : ''}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
        />
      )}

      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Remove condition"
        onClick={onRemove}
      >
        <Trash2 />
      </Button>
    </div>
  )
}

// ---- a group of rules -------------------------------------------------------

function GroupEditor({
  group,
  onChange,
  onRemove,
  depth,
}: {
  group: ListRuleGroup
  onChange: (next: ListRuleGroup) => void
  onRemove?: () => void
  depth: number
}) {
  const setCombinator = (c: ListCombinator) => onChange({ ...group, combinator: c })

  const updateChild = (i: number, next: ListRule | ListRuleGroup) =>
    onChange({ ...group, rules: group.rules.map((r, idx) => (idx === i ? next : r)) })

  const removeChild = (i: number) =>
    onChange({ ...group, rules: group.rules.filter((_, idx) => idx !== i) })

  const addRule = () => {
    const field = PERSON_LIST_FIELDS[0]?.key ?? 'first_name'
    onChange({
      ...group,
      rules: [...group.rules, { field, operator: defaultOperator(field), value: '' }],
    })
  }

  const addGroup = () =>
    onChange({
      ...group,
      rules: [...group.rules, { combinator: 'and', rules: [] }],
    })

  return (
    <div className="space-y-2 rounded-xl border border-border bg-card p-3">
      <div className="flex items-center justify-between">
        <div
          className="inline-flex overflow-hidden rounded-lg border border-border"
          role="group"
          aria-label="Match"
        >
          {(['and', 'or'] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCombinator(c)}
              className={
                'px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ' +
                (group.combinator === c ? 'bg-card-hover text-fg' : 'text-muted hover:text-fg')
              }
              aria-pressed={group.combinator === c}
            >
              {c === 'and' ? 'Match all' : 'Match any'}
            </button>
          ))}
        </div>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Remove group"
            onClick={onRemove}
          >
            <Trash2 />
          </Button>
        )}
      </div>

      {group.rules.length === 0 ? (
        <p className="px-1 py-2 text-sm text-dim">
          No conditions yet — add one to start filtering.
        </p>
      ) : (
        <div className="space-y-2">
          {group.rules.map((child, i) =>
            isRuleGroup(child) ? (
              <GroupEditor
                key={i}
                group={child}
                depth={depth + 1}
                onChange={(next) => updateChild(i, next)}
                onRemove={() => removeChild(i)}
              />
            ) : (
              <RuleRow
                key={i}
                rule={child}
                onChange={(next) => updateChild(i, next)}
                onRemove={() => removeChild(i)}
              />
            ),
          )}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button type="button" variant="secondary" size="sm" onClick={addRule}>
          <Plus />
          Add condition
        </Button>
        {depth < 2 && (
          <Button type="button" variant="ghost" size="sm" onClick={addGroup}>
            <Plus />
            Add group
          </Button>
        )}
      </div>
    </div>
  )
}

export function RuleBuilder({
  definition,
  onChange,
}: {
  definition: ListDefinition
  onChange: (next: ListDefinition) => void
}) {
  return (
    <GroupEditor
      group={definition}
      depth={0}
      onChange={(next) => onChange({ combinator: next.combinator, rules: next.rules })}
    />
  )
}
